import { format } from "date-fns";
import { MoreVertical, BanIcon } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { SiblingRankBadge } from "@/components/shared/SiblingRankBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { RankChangeIndicator } from "../RankChangeIndicator";
import type { RankTrackerKeyword } from "@/types";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";

interface KeywordDataRowProps {
  keyword: RankTrackerKeyword;
  isPublic: boolean;
  showActionsColumn: boolean;
  onStopTracking: (keywordId: number, keywordText: string) => void;
}

export function KeywordDataRow({
  keyword,
  isPublic,
  showActionsColumn,
  onStopTracking,
}: KeywordDataRowProps) {
  return (
    <TableRow key={keyword.id} className="hover:bg-transparent">
      <TableCell className="py-2 px-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-medium">{keyword.keyword}</span>
        </div>
      </TableCell>
      <TableCell className="py-2 px-3 text-xs">
        <div className="flex items-center gap-1">
          <span>{formatMetricNumber(keyword.searchVolume)}</span>
        </div>
      </TableCell>
      <TableCell className="py-2 px-3 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold">{keyword.currentRank ?? "—"}</span>
          {keyword.rankFromVariant && keyword.bestRankVariantAsin && (
            <SiblingRankBadge asin={keyword.bestRankVariantAsin} />
          )}
        </div>
      </TableCell>
      <TableCell className="py-2 px-3 text-xs">
        <RankChangeIndicator
          currentRank={keyword.currentRank}
          previousRank={keyword.previousRank}
        />
      </TableCell>
      <TableCell className="py-2 px-3 text-xs text-muted-foreground">
        {format(new Date(keyword.dateAdded), "MMM d, yyyy")}
      </TableCell>
      {showActionsColumn ? (
        <TableCell className="py-2 px-3 text-xs text-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onStopTracking(keyword.id, keyword.keyword)}
              >
                <BanIcon className="h-4 w-4 mr-2" />
                Stop Tracking
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      ) : null}
    </TableRow>
  );
}
